/*
 * Cerita Kami video modal, shared.
 *
 * This file is the modal that `sections/mh-stories.liquid` has always shipped. Its code is
 * unchanged; only where it lives has changed, and this comment is the only thing added.
 *
 * **Why it moved out of the section's javascript block.** Shopify wraps every section's
 * `{%- raw -%}{% javascript %}{%- endraw -%}` block in a guard of its own making:
 *
 *     if (!__sections__["mh-stories"] && !Shopify.designMode) return;
 *
 * so a section's script runs ONLY on pages that render that section. The bundle is theme-wide, but
 * the code inside it is not. That makes "reuse the existing modal from another section" impossible
 * while the definition lives in the block: the class is in the bundle on every page and defined on
 * exactly one of them. The stylesheet block has no such guard, which is why the modal's CSS did
 * reach other pages and only the behaviour was missing. Verified by reading the compiled
 * `compiled_assets/scripts.js`, not assumed. See docs/DECISIONS.md D62.
 *
 * Loaded with a plain script tag by every section that renders a modal: `mh-stories` (Cerita Kami)
 * and `mh-neelofa-video` (the Neelofa landing fallback). The define below is already guarded
 * against running twice, so two sections on one page load it once and define it once.
 */
/*
 * Cerita Kami — open a story's video in a modal instead of leaving the site.
 *
 * The card stays a real link with `target="_blank"`. This intercepts the click and shows the
 * video in a dialog; if it cannot — no `<dialog>` support, or a URL it has no player for — it
 * does nothing and the browser opens the link in a new tab. Either way the current page is never
 * navigated away from.
 *
 * Shopify concatenates every section's javascript block into one theme-wide bundle that runs on
 * every page, so all of this is inside an IIFE and everything hangs off the custom element. With
 * no `<mh-stories-modal>` in the document nothing here does anything.
 */
(function () {
  /**
   * Map a watch URL to something playable inside the modal.
   *
   * Returns `{ kind, src, title, wide }` where kind is 'iframe' for a provider's own player or
   * 'video' for a file we can play ourselves, or null for anything unrecognised, which is the
   * signal to leave the click alone and let the link open in a new tab.
   *
   * Order matters only in one place: a direct file is matched by extension before any host is
   * considered, so an .mp4 served from any domain plays natively rather than being handed to a
   * provider branch that would not recognise it.
   */
  function embedFor(rawUrl) {
    var u;
    try {
      u = new URL(rawUrl, window.location.href);
    } catch (e) {
      return null;
    }

    var host = u.hostname.replace(/^www\./, '');
    var seg = u.pathname.split('/').filter(Boolean);

    // A direct video file, wherever it is hosted. This is the cleanest case in the whole list:
    // no third party, no embed player, no external request beyond the file itself, and the
    // browser's own controls. Checked against the path only, so a query string cannot fool it.
    if (/\.(mp4|webm|mov|m4v)$/i.test(u.pathname)) {
      return { kind: 'video', src: u.href, title: 'Video', wide: false };
    }

    // Instagram. A normal post URL cannot be framed — only the /embed player can — and the
    // path shape varies: /reel/<id>, /reels/<id> (both are in use on this store right now),
    // /p/<id> for a feed post and /tv/<id> for IGTV.
    if (host === 'instagram.com' || host.slice(-14) === '.instagram.com') {
      var kind = { reel: 'reel', reels: 'reel', p: 'p', tv: 'tv' }[seg[0]];
      if (kind && seg[1]) {
        return { kind: 'iframe', src: 'https://www.instagram.com/' + kind + '/' + seg[1] + '/embed', title: 'Instagram', wide: false };
      }
      return null;
    }

    if (host === 'youtu.be' && seg[0]) {
      return { kind: 'iframe', src: 'https://www.youtube.com/embed/' + seg[0] + '?autoplay=1', title: 'YouTube', wide: true };
    }

    if (host === 'youtube.com' || host.slice(-12) === '.youtube.com') {
      var id = u.searchParams.get('v');
      var shorts = seg[0] === 'shorts';
      if (!id && (shorts || seg[0] === 'embed' || seg[0] === 'live')) id = seg[1];
      if (id) {
        return { kind: 'iframe', src: 'https://www.youtube.com/embed/' + id + '?autoplay=1', title: 'YouTube', wide: !shorts };
      }
      return null;
    }

    if (host === 'vimeo.com' || host.slice(-10) === '.vimeo.com') {
      var vid = seg.filter(function (s) { return /^\d+$/.test(s); })[0];
      if (vid) {
        return { kind: 'iframe', src: 'https://player.vimeo.com/video/' + vid + '?autoplay=1', title: 'Vimeo', wide: true };
      }
      return null;
    }

    // TikTok. The official iframe player takes the numeric video id, which only the long form
    // of the URL carries: /@user/video/<id>, or the older /v/<id>.html. A vm.tiktok.com or
    // vt.tiktok.com short link is a server side redirect and cannot be resolved from here
    // without a cross origin request, so it deliberately returns null and opens in a new tab.
    if (host === 'tiktok.com' || host.slice(-11) === '.tiktok.com') {
      var tik = null;
      var videoAt = seg.indexOf('video');
      if (videoAt !== -1 && /^\d+$/.test(seg[videoAt + 1] || '')) {
        tik = seg[videoAt + 1];
      } else if (seg[0] === 'v' && /^\d+/.test(seg[1] || '')) {
        tik = (seg[1].split('.')[0]);
      } else if (seg[0] === 'embed' && /^\d+$/.test(seg[seg.length - 1] || '')) {
        tik = seg[seg.length - 1];
      }
      if (tik) {
        return {
          kind: 'iframe',
          src: 'https://www.tiktok.com/player/v1/' + tik + '?music_info=1&description=0',
          title: 'TikTok',
          wide: false,
        };
      }
      return null;
    }

    // Facebook, through the official video plugin, which takes the original watch URL encoded
    // as `href`. It handles /<page>/videos/<id>, /watch/?v=<id> and /reel/<id>, and Facebook
    // resolves an fb.watch short link on its own side, which is why that one does not need the
    // id up front the way TikTok does.
    //
    // A video whose privacy settings forbid embedding renders Facebook's own "unavailable"
    // notice inside the player. That cannot be detected from here before opening, because the
    // iframe is cross origin; see docs/DECISIONS.md D59.
    if (host === 'facebook.com' || host.slice(-13) === '.facebook.com' || host === 'fb.watch') {
      var isFbVideo =
        host === 'fb.watch' ||
        seg.indexOf('videos') !== -1 ||
        seg[0] === 'reel' ||
        (seg[0] === 'watch' && u.searchParams.get('v'));
      if (isFbVideo) {
        return {
          kind: 'iframe',
          src:
            'https://www.facebook.com/plugins/video.php?href=' +
            encodeURIComponent(u.href) +
            '&show_text=false&autoplay=true',
          title: 'Facebook',
          wide: seg[0] !== 'reel',
        };
      }
      return null;
    }

    return null;
  }

  class MhStoriesModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.frame = this.querySelector('[data-mh-frame]');
      this.closeBtn = this.querySelector('[data-mh-close]');
      this.scope = this.closest('.mh-stories');

      // No dialog support: leave every link alone so they open in a new tab.
      if (!this.dialog || !this.frame || !this.scope || typeof this.dialog.showModal !== 'function') return;

      this.onClick = this.onClick.bind(this);
      this.onClose = this.onClose.bind(this);

      this.scope.addEventListener('click', this.onClick);
      this.closeBtn.addEventListener('click', () => this.dialog.close());

      // Clicking the backdrop targets the dialog itself — its children are the player and the
      // close button, so a click that lands on the dialog is a click outside the content.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });

      // Fires for the close button, the backdrop and Esc alike, so teardown lives in one place.
      this.dialog.addEventListener('close', this.onClose);

      // Advertise the real behaviour only on the links that will actually get it.
      this.scope.querySelectorAll('a.mh-stories__card[href]').forEach((a) => {
        if (embedFor(a.href)) a.setAttribute('aria-haspopup', 'dialog');
      });
    }

    disconnectedCallback() {
      if (this.scope) this.scope.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const link = event.target.closest('a.mh-stories__card[href]');
      if (!link || !this.scope.contains(link)) return;

      // A middle click or a modified click is the visitor deliberately asking for a new tab.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const embed = embedFor(link.href);
      if (!embed) return; // Unrecognised host — let target="_blank" do its job.

      event.preventDefault();
      this.open(embed, link);
    }

    open(embed, trigger) {
      this.trigger = trigger;
      this.dialog.classList.toggle('is-wide', embed.wide);

      // A file we play ourselves gets its own frame shape, because an mp4 can be either
      // orientation and only the file knows which. `object-fit: contain` in a generous box
      // suits both, where the fixed portrait and 16:9 frames each suit only one.
      this.dialog.classList.toggle('is-video', embed.kind === 'video');

      this.frame.replaceChildren(embed.kind === 'video' ? this.buildVideo(embed) : this.buildIframe(embed));

      // showModal() already makes the rest of the page inert; this stops the body scrolling
      // behind the backdrop, which not every browser does on its own. Set inline so this
      // section adds no rule outside its own namespace.
      this.prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      this.dialog.showModal();
      this.closeBtn.focus();
    }

    buildIframe(embed) {
      const iframe = document.createElement('iframe');
      iframe.className = 'mh-stories__iframe';
      iframe.src = embed.src;
      iframe.title = embed.title;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      return iframe;
    }

    buildVideo(embed) {
      const video = document.createElement('video');
      video.className = 'mh-stories__video';
      video.src = embed.src;
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('aria-label', embed.title);

      // Try with sound first, and fall back to muted if the browser refuses. Every browser
      // blocks unmuted autoplay without a prior gesture, and the promise rejecting is the only
      // way to find out; muting and retrying is what turns a silent failure into a playing
      // video. If even that is refused, the controls are right there.
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          video.muted = true;
          const retry = video.play();
          if (retry && typeof retry.catch === 'function') retry.catch(() => {});
        });
      }
      return video;
    }

    onClose() {
      // Pause and detach the source before emptying, so a large file stops downloading at once
      // rather than finishing its current buffer.
      const video = this.frame.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }

      // Emptying the frame is what actually stops playback — a hidden iframe keeps going.
      this.frame.replaceChildren();
      document.body.style.overflow = this.prevOverflow || '';
      if (this.trigger) {
        this.trigger.focus();
        this.trigger = null;
      }
    }
  };

  if (!customElements.get('mh-stories-modal')) {
    customElements.define('mh-stories-modal', MhStoriesModal);
  }
})();
